import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { queryKeys } from '@/api/query-keys'
import type { SignedElementReference, SpreadCheckpoint } from '@/api/types/spread-checkpoint'
import { elementsService } from '@/api/services/elements.service'
import { readProtoCoord } from '@/lib/elements/decode-proto-path-coords'
import {
  readIgnoreTextWrapFromPageElementBase,
  readIsFrozenFromPageElementBase,
  readIsLockedFromPageElementBase,
} from '@/lib/elements/read-page-element-base-flags'
import { readPageElementEditorRectFromBase } from '@/lib/elements/read-page-element-editor-rect'
import { readWireShapePaint } from '@/lib/elements/read-wire-shape-paint'
import { readTextOnPathConfigFrom } from '@/lib/elements/text-on-path-element'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsForSides, pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import {
  SIDES_ELEMENT_KIND_ID,
  defaultSidesForType,
  innerRatioFromInsetPercent,
  insetPercentFromInner,
  starShapeTypeFromInner,
} from '@/lib/object-effects/sides'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { readShapeTextParagraphsFromDecoded } from '@/lib/shapes/read-shape-text-paragraphs'
import { readFractionalIndexFromDecodedRecord } from '@/lib/checkpoint/element-wrapper-fields'
import { rotateVector } from '@/lib/transforms/geometry'
import { decodeUnknownElementFromBuffer, initializeDecoder } from '@/lib/protobuf/decoder'
import { isDebugEnabled } from '@/config/config'
import { logger } from '@/utils/logger'
import { useWebSocket } from '@/hooks/use-websocket'

type AnyRecord = Record<string, unknown>

export type UseMultiplayerElementsSyncArgs = {
  spreadId: string
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  enabled?: boolean
  /**
   * Fired after a remote editor deletes a text box (WMPCM-6868). This hook removes the box from
   * TextManager but has no access to the text-chain topology, which lives in `home.tsx` — the
   * caller uses this to splice the deleted uid out of its own chain state so a later local edit
   * doesn't re-persist a dangling link. `uid` is the numeric wire uid (looked up before removal),
   * not the `objectId` UUID — chain membership keys off `uid`, so both are passed.
   */
  onRemoteTextBoxDeleted?: (objectId: string, uid?: number) => void
}

type ElementTargetLayer = 'UserLayer' | 'MasterLayer' | 'userLayer' | 'masterLayer'

type ElementEventKind = 'added' | 'updated' | 'deleted'

const OVAL_ELEMENT_KIND_ID = 8

function asRecord(value: unknown): AnyRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as AnyRecord
}

function asString(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function getField(obj: AnyRecord, keys: string[]): unknown {
  for (const k of keys) {
    if (k in obj) return obj[k]
  }

  return undefined
}

function decodeBase64Json(value: string): unknown | null {
  try {
    // Support both standard and URL-safe base64.
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')

    const jsonStr = atob(normalized)

    return JSON.parse(jsonStr)
  } catch {
    return null
  }
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

function inferKindIdFromDecodedData(decodedData: unknown): number {
  const r = asRecord(decodedData)

  if (!r) return 1

  // Check for an explicit kindId (StowedElement wrapper).
  const explicit = asNumber(r.kindId) ?? asNumber(r.KindId)

  if (explicit != null) return explicit

  // Fallback: infer from path commands at top level or nested under element fields.
  const path = asRecord(r.path) ?? asRecord(r.Path)
  const commands = path ? (path.commands ?? path.Commands) : null

  if (Array.isArray(commands) && commands.length > 0) return 2

  // Check if element-type data is nested (StowedElement format).
  if (r.irregularPolygonElement || r.irregularPolygon || r.irregular_polygon) return 2
  if (r.rectangleElement || r.rectangle_element) return 1
  if (r.pageNumberElement || r.page_number_element) return 5
  if (r.textOnPathElement || r.text_on_path_element) return 6
  if (r.groupElement || r.group_element) return 7
  if (r.ovalElement || r.oval_element || r.OvalElement) return OVAL_ELEMENT_KIND_ID
  if (r.regularPolygonElement || r.regular_polygon_element || r.RegularPolygonElement) return 10

  return 1
}

function normalizeShapeType(value: unknown): ShapeType | null {
  if (typeof value !== 'string') return null

  const allowed: Set<ShapeType> = new Set([
    'square',
    'circle',
    'triangle',
    'hexagon',
    'pentagon',
    'star',
    'heart',
    'starone',
    'startwo',
    'path',
  ])
  // Mirror checkpoint-to-shapes by accepting any casing.
  const lower = value.toLowerCase()

  return allowed.has(lower as ShapeType) ? (lower as ShapeType) : null
}

/**
 * For `RegularPolygonElement` (kindId 10), reconstruct the shape type from protobuf-native
 * `inner` (star variant ratio) and `sides` count when `editorShape` metadata is missing.
 */
function inferRegularPolygonShapeType(record: AnyRecord): ShapeType {
  const innerNum = finiteNumber(record.inner ?? record.Inner)
  const star = innerNum != null ? starShapeTypeFromInner(innerNum) : null

  if (star) return star

  const sides = finiteNumber(record.sides ?? record.Sides)

  if (sides === 3) return 'triangle'
  if (sides === 5) return 'pentagon'
  if (sides === 6) return 'hexagon'

  return 'hexagon'
}

function hasCurveToFields(cmd: Record<string, unknown>): boolean {
  const hasEnd = cmd.x != null && cmd.y != null

  if (!hasEnd) return false

  const hasFirstControl =
    cmd.controlX != null ||
    cmd.control_x != null ||
    cmd.ControlX != null ||
    cmd.controlY != null ||
    cmd.control_y != null ||
    cmd.ControlY != null
  const hasSecondControl =
    cmd.deltaX != null ||
    cmd.deltaY != null ||
    cmd.delta_x != null ||
    cmd.delta_y != null ||
    cmd.DeltaX != null ||
    cmd.DeltaY != null ||
    cmd.controlX2 != null ||
    cmd.controlY2 != null ||
    cmd.control_x2 != null ||
    cmd.control_y2 != null ||
    cmd.ControlX2 != null ||
    cmd.ControlY2 != null

  return Boolean(hasFirstControl || hasSecondControl)
}

function getPathCommandsFromDecodedData(decodedData: unknown): PathCommand[] | null {
  const r = asRecord(decodedData)

  if (!r) return null

  const path = asRecord(r.path) ?? asRecord(r.Path)

  if (!path) return null

  const commands = asArray(path.commands) ?? asArray(path.Commands)

  if (!commands) return null

  const out: PathCommand[] = []

  for (const c of commands) {
    const cmd = asRecord(c) as Record<string, unknown> | null

    if (!cmd) continue

    const moveTo = asRecord(cmd.moveTo) ?? asRecord(cmd.move_to) ?? asRecord(cmd.MoveTo)

    if (moveTo) {
      // protobuf-toObject often omits zeros; default missing coords to 0.
      const x = moveTo.x == null ? 0 : finiteNumber(moveTo.x)
      const y = moveTo.y == null ? 0 : finiteNumber(moveTo.y)

      if (x != null && y != null) out.push({ moveTo: { x, y } })
      continue
    }

    const lineTo = asRecord(cmd.lineTo) ?? asRecord(cmd.line_to) ?? asRecord(cmd.LineTo)

    if (lineTo) {
      const x = lineTo.x == null ? 0 : finiteNumber(lineTo.x)
      const y = lineTo.y == null ? 0 : finiteNumber(lineTo.y)

      if (x != null && y != null) out.push({ lineTo: { x, y } })
      continue
    }

    const curveTo =
      asRecord(cmd.curveTo) ??
      asRecord(cmd.curve_to) ??
      asRecord(cmd.CurveTo) ??
      (hasCurveToFields(cmd) ? cmd : null)

    if (curveTo) {
      const c = curveTo as Record<string, unknown>
      const controlX = readProtoCoord(c.controlX ?? c.control_x ?? c.ControlX)
      const controlY = readProtoCoord(c.controlY ?? c.control_y ?? c.ControlY)
      const x = readProtoCoord(c.x)
      const y = readProtoCoord(c.y)
      const c2x = readProtoCoord(
        c.deltaX ?? c.delta_x ?? c.DeltaX ?? c.controlX2 ?? c.control_x2 ?? c.ControlX2
      )
      const c2y = readProtoCoord(
        c.deltaY ?? c.delta_y ?? c.DeltaY ?? c.controlY2 ?? c.control_y2 ?? c.ControlY2
      )

      out.push({
        curveTo: {
          controlX,
          controlY,
          x,
          y,
          deltaX: c2x,
          deltaY: c2y,
        },
      })
    }
  }

  return out.length > 0 ? out : null
}

/**
 * Shifts wire-format path commands (center-relative, matching legacy `page-editor`'s own
 * convention — `ODPath.prototype.addSegmentToPXML`, odeditorsvg.js:15091, subtracts
 * `width/2`/`height/2` on save) back to this app's top-left-relative convention (0..width ×
 * 0..height). Mirrors `offsetPathCommands` in `lib/checkpoint/checkpoint-to-shapes.ts` — inverse
 * of the shift `stowed-element-builder.ts`'s `convertPathCommands` applies on save.
 */
function offsetPathCommands(commands: PathCommand[], dx: number, dy: number): PathCommand[] {
  return commands.map((cmd) => {
    if ('moveTo' in cmd) return { moveTo: { x: cmd.moveTo.x + dx, y: cmd.moveTo.y + dy } }
    if ('lineTo' in cmd) return { lineTo: { x: cmd.lineTo.x + dx, y: cmd.lineTo.y + dy } }
    if ('curveTo' in cmd) {
      const ct = cmd.curveTo

      return {
        curveTo: {
          controlX: ct.controlX + dx,
          controlY: ct.controlY + dy,
          x: ct.x + dx,
          y: ct.y + dy,
          ...(typeof ct.deltaX === 'number' ? { deltaX: ct.deltaX + dx } : {}),
          ...(typeof ct.deltaY === 'number' ? { deltaY: ct.deltaY + dy } : {}),
          ...(typeof ct.controlX2 === 'number' ? { controlX2: ct.controlX2 + dx } : {}),
          ...(typeof ct.controlY2 === 'number' ? { controlY2: ct.controlY2 + dy } : {}),
        },
      }
    }

    return cmd
  })
}

/**
 * Known StowedElement field names per kindId.
 * Covers TypeScript naming, protobuf JSON (camelCase), and snake_case variants
 * so we can unwrap element-type data regardless of which convention the
 * backend/WebSocket uses.
 */
const STOWED_ELEMENT_FIELDS: Record<number, string[]> = {
  1: ['rectangleElement', 'rectangle_element', 'RectangleElement'],
  2: [
    'irregularPolygonElement',
    'irregularPolygon',
    'irregular_polygon',
    'IrregularPolygonElement',
  ],
  5: ['pageNumberElement', 'page_number_element', 'PageNumberElement'],
  6: ['textOnPathElement', 'text_on_path_element', 'TextOnPathElement'],
  7: ['groupElement', 'group_element', 'GroupElement'],
  [OVAL_ELEMENT_KIND_ID]: ['ovalElement', 'oval_element', 'OvalElement'],
  9: ['polylineElement', 'polyline_element', 'PolylineElement'],
  10: ['regularPolygonElement', 'regular_polygon_element', 'RegularPolygonElement'],
}

/**
 * If `data` is a full StowedElement (base is nested under a type-specific
 * field like `irregularPolygonElement`), extract and merge the inner
 * element-type data with top-level properties (e.g. `editorShape`).
 * Returns the original record unchanged when `base` is already at the top.
 */
function unwrapElementData(data: AnyRecord, kindId: number): AnyRecord {
  if (data.base || data.Base) return data

  const candidates = STOWED_ELEMENT_FIELDS[kindId]

  if (!candidates) return data

  for (const field of candidates) {
    const nested = asRecord(data[field])

    if (nested && (nested.base || nested.Base)) {
      return { ...data, ...nested }
    }
  }

  return data
}

/**
 * Reads a photo fill placed inside a shape (kindId 1, 2, 9 closed, 10) from `content.image` —
 * mirrors `readShapeFillImage` in checkpoint-to-shapes.ts, so a remote edit to a photoFill shape
 * (standalone drop or shape fill — both now use this wire shape) renders live for other editors
 * instead of only appearing after their next full reload.
 */
function readShapeFillImage(record: AnyRecord): ShapePhotoFill | undefined {
  const content = asRecord(record.content) ?? asRecord(record.Content)
  const image = content ? (asRecord(content.image) ?? asRecord(content.Image)) : undefined

  if (!image) return undefined

  const imageId = image.imageId ?? image.image_id ?? image.ImageId
  const imageType = image.imageType ?? image.image_type ?? image.ImageType
  const pezId = image.pezId ?? image.pez_id ?? image.PezId

  // A photo fill carries its real id in `imageId` and leaves `pezId` empty; a ClikArt fill is the
  // opposite (`imageId: ''`, real id in `pezId` — see build-clikart-shape-fill.ts). Requiring only
  // `imageId` here silently discarded every ClikArt fill for other connected editors — see the
  // identical fix and full explanation in `readShapeFillImage` in checkpoint-to-shapes.ts.
  const hasImageId = typeof imageId === 'string' && imageId !== ''
  const hasPezId = typeof pezId === 'string' && pezId !== ''

  if (!hasImageId && !hasPezId) return undefined
  const previewUrl = image.imagePreviewUrl ?? image.image_preview_url ?? image.ImagePreviewUrl
  const displayName = image.imageDisplayName ?? image.image_display_name ?? image.ImageDisplayName

  // Rotation of the photo CONTENT within its frame (crop mode) — independent of the shape's own
  // rotation. See `buildShapeImageFillContent` in stowed-element-builder.ts.
  const rotation = finiteNumber(image.rotation ?? image.Rotation)
  const rawOffsetX = finiteNumber(image.x ?? image.X) ?? 0
  const rawOffsetY = finiteNumber(image.y ?? image.Y) ?? 0
  // `x/y` are legacy's rotate-in-place-pivot offset, not Larry's raw pan offset when rotation is
  // nonzero — un-rotate by -rotation to recover it. See `readShapeFillImage` in
  // checkpoint-to-shapes.ts and `buildShapeImageFillContent` in stowed-element-builder.ts.
  const { x: offsetX, y: offsetY } = rotateVector(
    { x: rawOffsetX, y: rawOffsetY },
    -(rotation ?? 0)
  )
  const rawWidth = finiteNumber(image.width ?? image.Width) ?? undefined
  const rawHeight = finiteNumber(image.height ?? image.Height) ?? undefined
  // Legacy's own encoding of a horizontally/vertically-flipped image
  // (`ODImage.flipx`/`.flipy`) is a negative width/height, not a dedicated flag — see
  // `ShapePhotoFill.flippedX`/`.flippedY` doc comments.
  const flippedX = rawWidth != null && rawWidth < 0
  const flippedY = rawHeight != null && rawHeight < 0
  const persistedRenderedWidth = rawWidth != null ? Math.abs(rawWidth) : undefined
  const persistedRenderedHeight = rawHeight != null ? Math.abs(rawHeight) : undefined

  return {
    imageId: typeof imageId === 'string' ? imageId : '',
    imageType: typeof imageType === 'string' ? imageType : 'Candid',
    pezId: typeof pezId === 'string' ? pezId : '',
    previewUrl: typeof previewUrl === 'string' ? previewUrl : '',
    displayName: typeof displayName === 'string' ? displayName : '',
    offsetX,
    offsetY,
    scale: 1,
    ...(flippedX ? { flippedX } : {}),
    ...(flippedY ? { flippedY } : {}),
    ...(persistedRenderedWidth !== undefined ? { persistedRenderedWidth } : {}),
    ...(persistedRenderedHeight !== undefined ? { persistedRenderedHeight } : {}),
    ...(rotation != null ? { rotation } : {}),
  }
}

function decodedDataToShape(
  objectId: string,
  decodedData: unknown,
  kindId: number
): ShapeData | null {
  const raw = asRecord(decodedData)

  if (!raw) return null

  const r = unwrapElementData(raw, kindId)

  const base = asRecord(r.base) ?? asRecord(r.Base)

  if (!base) return null

  const rect = readPageElementEditorRectFromBase(base as Record<string, unknown>)

  if (!rect) return null

  const { x, y, width, height } = rect

  const photoFill: ShapePhotoFill | undefined = readShapeFillImage(r)

  const rotation = finiteNumber(base.rotation ?? base.Rotation) ?? 0
  const locked = readIsLockedFromPageElementBase(base as Record<string, unknown>)
  const frozen = readIsFrozenFromPageElementBase(base as Record<string, unknown>)
  const ignoreTextWrap = readIgnoreTextWrapFromPageElementBase(base as Record<string, unknown>)
  const transparencyRaw = finiteNumber(base.transparency ?? base.Transparency) ?? 0
  const baseOpacity = Math.max(
    0,
    Math.min(1, 1 - Math.max(0, Math.min(100, transparencyRaw)) / 100)
  )
  // Text-link chain key for a shape text frame (WMPCM-7988) — dropping it here would dangle the
  // shape's chain membership on the next remote edit. uid 0 means "unset" on the wire.
  const uidRaw = finiteNumber(base.uid ?? base.Uid)
  const uid = uidRaw !== null && uidRaw > 0 ? uidRaw : undefined

  const editorShape = asRecord(r.editorShape) ?? asRecord(r.EditorShape)
  const editorShapeType = normalizeShapeType(editorShape?.type ?? editorShape?.Type)
  // For regular polygons, fall back to inferring the shape from `sides` + `inner` when
  // `editorShape` is unavailable (e.g. backend dropped the frontend-only metadata).
  const resolvedShapeType: ShapeType =
    editorShapeType ??
    (kindId === SIDES_ELEMENT_KIND_ID
      ? inferRegularPolygonShapeType(r)
      : kindId === OVAL_ELEMENT_KIND_ID
        ? 'circle'
        : 'square')
  let pathCommands: PathCommand[] | null = null
  const editorInsetRaw = finiteNumber(editorShape?.inset ?? editorShape?.Inset)
  const innerRaw = finiteNumber(r.inner ?? r.Inner)
  const insetForShape =
    kindId === SIDES_ELEMENT_KIND_ID
      ? editorInsetRaw != null
        ? Math.max(0, Math.min(100, Math.round(editorInsetRaw)))
        : innerRaw != null
          ? insetPercentFromInner(innerRaw)
          : undefined
      : undefined

  if (kindId === 2) {
    pathCommands = getPathCommandsFromDecodedData(r)

    if (pathCommands) {
      pathCommands = offsetPathCommands(pathCommands, width / 2, height / 2)
    }
  } else if (kindId === SIDES_ELEMENT_KIND_ID) {
    const sidesRaw = finiteNumber(r.sides ?? r.Sides)
    const n =
      sidesRaw != null
        ? Math.max(3, Math.min(100, Math.round(sidesRaw)))
        : defaultSidesForType(resolvedShapeType)

    pathCommands = pathCommandsForSides(
      resolvedShapeType,
      n,
      width,
      height,
      insetForShape != null ? innerRatioFromInsetPercent(insetForShape) : undefined,
      // Mirrors `checkpointToShapes`: a RegularPolygonElement with 4 sides is a diamond, so a
      // remote editor's diamond must not render here as an axis-aligned rectangle.
      { asRegularPolygon: true }
    )
  }

  const path = pathCommands
    ? pathCommandsToSvgPath(pathCommands, { close: kindId !== 9 })
    : undefined

  const isSupportedKind =
    kindId === 1 ||
    kindId === 2 ||
    kindId === OVAL_ELEMENT_KIND_ID ||
    kindId === SIDES_ELEMENT_KIND_ID
  const fillRaw = editorShape?.fill ?? editorShape?.Fill
  const strokeRaw = editorShape?.stroke ?? editorShape?.Stroke
  const strokeWidthRaw = editorShape?.strokeWidth ?? editorShape?.StrokeWidth
  const opacityRaw = editorShape?.opacity ?? editorShape?.Opacity

  const fractionalIndex = readFractionalIndexFromDecodedRecord(r)
  const paragraphs = readShapeTextParagraphsFromDecoded(
    {
      ...r,
      ...(editorShape ? { editorShape } : {}),
    },
    kindId
  )

  // editorShape (the frontend-only carrier for fillRaw/strokeRaw/strokeWidthRaw above) is
  // dropped by the backend on a real save, so a text frame reverts to the "unset shape"
  // visible-gray/black default below unless it survived as a live remote edit still carrying
  // its editorShape — see the matching fix in checkpoint-to-shapes.ts for the full account.
  const isTextFrameShape = isShapeTextFrame({
    kindId,
    type: resolvedShapeType,
    path,
    paragraphs,
    photoFill,
  } as ShapeData)

  // `isTextFrameShape` is true for ANY shape carrying `paragraphs`, so it cannot tell a Type-tool
  // frame (which really should be transparent) from a rectangle the user typed into (which should
  // keep its paint). Once `editorShape` is gone — which is always, on anything that has been
  // through the backend — that made every shape-with-text decode invisible. `fillStyle`/`lineStyle`
  // are the durable NSD paint attributes, so ask the wire first. `=== false` is load-bearing:
  // `readWireShapePaint` returns `null` for kinds that do not record the attribute (8/9/10), and
  // unknown must mean "keep the paint". Mirrors `checkpointToShapes` — keep the two in sync.
  const wirePaint = readWireShapePaint(r, kindId)
  // A fill picked as None (any shape, not just a text frame) is saved with no `fillStyle`, the same
  // as legacy, which also renders a missing `<FS>` as no fill.
  const wireSaysNoFill = wirePaint.hasVisibleFill === false
  const wireSaysNoStroke = isTextFrameShape && wirePaint.hasVisibleStroke === false

  const fill =
    typeof fillRaw === 'string'
      ? fillRaw
      : wireSaysNoFill
        ? 'transparent'
        : isSupportedKind
          ? 'rgba(229, 231, 235, 1)'
          : 'rgba(249, 115, 22, 0.08)'
  const stroke =
    typeof strokeRaw === 'string'
      ? strokeRaw
      : wireSaysNoStroke
        ? 'transparent'
        : isSupportedKind
          ? '#000000'
          : 'rgba(249, 115, 22, 1)'
  const strokeWidth =
    finiteNumber(strokeWidthRaw) ??
    (wireSaysNoStroke
      ? 0
      : isTextFrameShape
        ? // A text frame that kept its stroke restores the exact persisted weight.
          (wirePaint.strokeWeight ?? 1)
        : isSupportedKind
          ? 1
          : 2)
  const resolvedOpacity = finiteNumber(opacityRaw) ?? baseOpacity
  const textOnPath = readTextOnPathConfigFrom(r, raw)

  let roundedCorner: number | undefined

  if (kindId === 1) {
    const rc = finiteNumber(r.roundedCorner ?? r.RoundedCorner)

    if (rc != null) {
      roundedCorner = Math.max(0, Math.min(100, rc))
    }
  }

  let sides: number | undefined

  if (kindId === SIDES_ELEMENT_KIND_ID) {
    const sr = finiteNumber(r.sides ?? r.Sides)

    sides =
      sr != null
        ? Math.max(3, Math.min(100, Math.round(sr)))
        : defaultSidesForType(resolvedShapeType)
  }

  return {
    id: objectId,
    ...(fractionalIndex ? { fractionalIndex } : {}),
    type: resolvedShapeType,
    x,
    y,
    width,
    height,
    ...(pathCommands ? { pathCommands } : {}),
    ...(path ? { path } : {}),
    rotation,
    fill,
    ...(wirePaint.fillColorId != null ? { fillColorId: wirePaint.fillColorId } : {}),
    ...(wirePaint.fillTint != null ? { fillTint: wirePaint.fillTint } : {}),
    stroke,
    strokeWidth,
    opacity: resolvedOpacity,
    locked,
    frozen,
    ignoreTextWrap,
    kindId,
    ...(paragraphs ? { paragraphs } : {}),
    ...(uid !== undefined ? { uid } : {}),
    ...(roundedCorner !== undefined ? { roundedCorner } : {}),
    ...(sides !== undefined ? { sides } : {}),
    ...(insetForShape !== undefined ? { inset: insetForShape } : {}),
    ...(photoFill ? { photoFill } : {}),
    // Text on a Path rides the host's element rather than being promoted to `kindId: 6`
    // (WMPCM-9542), so a remote editor's text arrives on the same row as the shape it runs along.
    // Mirrors `checkpointToShapes` — keep the two in sync.
    ...(textOnPath ? { textOnPath } : {}),
  }
}

function normalizeTargetLayer(
  layer: ElementTargetLayer | null
): 'userLayer' | 'masterLayer' | null {
  if (!layer) return null
  const v = String(layer).toLowerCase()

  if (v === 'userlayer' || v === 'userlayer'.toLowerCase()) return 'userLayer'
  if (v === 'masterlayer' || v === 'masterlayer'.toLowerCase()) return 'masterLayer'
  if (v === 'userlayer' || v === 'userlayer') return 'userLayer'
  if (v === 'masterlayer' || v === 'masterlayer') return 'masterLayer'
  if (v === 'userlayer') return 'userLayer'
  if (v === 'masterlayer') return 'masterLayer'
  // also accept existing casing variants
  if (layer === 'userLayer' || layer === 'UserLayer') return 'userLayer'
  if (layer === 'masterLayer' || layer === 'MasterLayer') return 'masterLayer'
  return null
}

function detectEventKind(event: string | undefined): ElementEventKind | null {
  const e = (event ?? '').toLowerCase()

  if (!e) return null
  if (e.includes('elementadd')) return 'added'
  if (e.includes('elementupdat')) return 'updated'
  if (e.includes('elementdelet')) return 'deleted'
  return null
}

function inferEventKindFromPayload(payload: AnyRecord): ElementEventKind | null {
  // Your payloads always contain ElementObjectId. Updates contain ElementVersionId + ElementSignedUrl.
  const hasObjectId = Boolean(
    asString(getField(payload, ['ElementObjectId', 'elementObjectId', 'ObjectId', 'objectId']))
  )

  if (!hasObjectId) return null

  const hasPositionIndex =
    typeof asNumber(getField(payload, ['ElementPositionIndex', 'elementPositionIndex'])) ===
    'number'

  if (hasPositionIndex) return 'added'

  const hasVersionId = Boolean(
    asString(getField(payload, ['ElementVersionId', 'elementVersionId', 'VersionId', 'versionId']))
  )
  const hasSignedUrl = Boolean(
    asString(getField(payload, ['ElementSignedUrl', 'elementSignedUrl', 'SignedUrl', 'signedUrl']))
  )

  if (hasVersionId && hasSignedUrl) return 'updated'

  return null
}

type ParsedElementEvent = {
  kind: ElementEventKind
  objectId: string
  versionId: string | null
  signedUrl: string | null
  decodedData: unknown | null
  inferredKindId: number | null
  /** Explicit kindId from the WebSocket payload (elementKindId). */
  explicitKindId: number | null
  positionIndex: number | null
  targetLayer: 'userLayer' | 'masterLayer' | null
  checkpointSpreadId: string | null
  checkpointVersionId: string | null
}

function parseElementEvent(kind: ElementEventKind, payload: AnyRecord): ParsedElementEvent | null {
  const objectId = asString(
    getField(payload, ['ElementObjectId', 'elementObjectId', 'ObjectId', 'objectId'])
  )

  if (!objectId) return null

  const versionId = asString(
    getField(payload, ['ElementVersionId', 'elementVersionId', 'VersionId', 'versionId'])
  )
  const signedUrl = asString(
    getField(payload, ['ElementSignedUrl', 'elementSignedUrl', 'SignedUrl', 'signedUrl'])
  )
  const targetLayerRaw = asString(
    getField(payload, ['ElementTargetLayer', 'elementTargetLayer', 'TargetLayer', 'targetLayer'])
  ) as ElementTargetLayer | null
  const targetLayer = normalizeTargetLayer(targetLayerRaw)
  const positionIndex = asNumber(
    getField(payload, ['ElementPositionIndex', 'elementPositionIndex'])
  )
  const checkpointVersionId = asString(
    getField(payload, ['CheckpointVersionId', 'checkpointVersionId'])
  )
  const checkpointSpreadId = asString(
    getField(payload, ['CheckpointSpreadId', 'checkpointSpreadId'])
  )
  const explicitKindId = asNumber(
    getField(payload, ['ElementKindId', 'elementKindId', 'KindId', 'kindId'])
  )

  const objectData = asString(
    getField(payload, ['ElementObjectData', 'elementObjectData', 'ObjectData', 'objectData'])
  )

  // elementObjectData may be Base64-encoded JSON (inline for elements < 2KB)
  // or direct JSON string.
  let decodedData: unknown | null = null

  if (objectData) {
    decodedData = decodeBase64Json(objectData)
    if (decodedData == null) {
      try {
        decodedData = JSON.parse(objectData)
      } catch {
        // not valid JSON either
      }
    }
  }

  const inferredKindId = decodedData ? inferKindIdFromDecodedData(decodedData) : null

  return {
    kind,
    objectId,
    versionId,
    signedUrl,
    decodedData,
    inferredKindId,
    explicitKindId,
    positionIndex,
    targetLayer,
    checkpointSpreadId,
    checkpointVersionId,
  }
}

function patchCheckpointCache(
  prev: SpreadCheckpoint,
  evt: ParsedElementEvent,
  resolved: { decodedData: unknown | null; kindId: number | null }
): SpreadCheckpoint {
  const kindId = resolved.kindId ?? evt.inferredKindId ?? 1
  const versionId = evt.versionId ?? ''
  const signedUrl = evt.signedUrl ?? ''

  const next: SpreadCheckpoint = {
    ...prev,
    decodedAt: new Date().toISOString(),
    metadata: { ...(prev.metadata as AnyRecord) },
    elements: prev.elements,
    elementReferences: prev.elementReferences
      ? [...prev.elementReferences]
      : prev.elementReferences,
  }

  // Patch elementReferences (best-effort)
  if (next.elementReferences) {
    const idx = next.elementReferences.findIndex((r) => r.objectId === evt.objectId)

    const ref: SignedElementReference = {
      objectId: evt.objectId,
      versionId: versionId || (idx >= 0 ? next.elementReferences[idx].versionId : ''),
      kindId,
      signedUrl: signedUrl || (idx >= 0 ? next.elementReferences[idx].signedUrl : ''),
      signedExpiration: idx >= 0 ? next.elementReferences[idx].signedExpiration : '',
    }

    if (idx >= 0) {
      next.elementReferences[idx] = { ...next.elementReferences[idx], ...ref }
    } else if (evt.kind === 'added') {
      next.elementReferences.push(ref)
    }
  }

  // Patch NsDocument element entry (best-effort; keep it minimal to avoid heavy churn)
  const metadata = next.metadata as AnyRecord

  const nsDoc = (metadata.nsDocument ?? metadata.NsDocument) as unknown

  const nsDocRec = asRecord(nsDoc)

  if (!nsDocRec) {
    if (isDebugEnabled()) {
      logger.debug('multiplayer: no NsDocument found; patched elementReferences only')
    }

    return next
  }

  // Ensure nested structure exists
  const pasteboardElements = (asRecord(nsDocRec.pasteboardElements) ??
    asRecord(nsDocRec.PasteboardElements) ??
    {}) as AnyRecord

  const userLayer = (asRecord(pasteboardElements.userLayer) ??
    asRecord(pasteboardElements.UserLayer) ??
    {}) as AnyRecord

  const elements = (
    Array.isArray(userLayer.elements)
      ? [...(userLayer.elements as unknown[])]
      : Array.isArray(userLayer.Elements)
        ? [...(userLayer.Elements as unknown[])]
        : []
  ) as unknown[]

  const elementIdx = elements.findIndex((el) => {
    const rec = asRecord(el)

    if (!rec) return false

    const oid = asString(rec.ObjectId ?? rec.objectId)

    return oid === evt.objectId
  })

  if (evt.kind === 'deleted') {
    if (elementIdx >= 0) {
      elements.splice(elementIdx, 1)
    }
  } else {
    const record: AnyRecord = elementIdx >= 0 ? { ...(asRecord(elements[elementIdx]) ?? {}) } : {}

    record.ObjectId = evt.objectId
    if (versionId) record.VersionId = versionId
    if (signedUrl) record.SignedUrl = signedUrl
    record.KindId = kindId
    if (resolved.decodedData != null) record.decodedData = resolved.decodedData

    if (elementIdx >= 0) {
      elements[elementIdx] = record
    } else if (evt.kind === 'added') {
      const insertAt =
        typeof evt.positionIndex === 'number' &&
        Number.isFinite(evt.positionIndex) &&
        evt.positionIndex >= 0 &&
        evt.positionIndex <= elements.length
          ? evt.positionIndex
          : elements.length

      elements.splice(insertAt, 0, record)
    }
  }

  // Rebuild minimal nesting using same key names present in the current doc.
  const nextNsDoc: AnyRecord = { ...nsDocRec }
  const pbKey = 'pasteboardElements' in nsDocRec ? 'pasteboardElements' : 'PasteboardElements'
  const ulKey = 'userLayer' in pasteboardElements ? 'userLayer' : 'UserLayer'
  const elKey = 'elements' in userLayer ? 'elements' : 'Elements'

  const nextUserLayer: AnyRecord = { ...userLayer, [elKey]: elements }

  nextNsDoc[pbKey] = { ...pasteboardElements, [ulKey]: nextUserLayer }

  if ('nsDocument' in metadata) metadata.nsDocument = nextNsDoc
  else metadata.NsDocument = nextNsDoc

  return next
}

export function useMultiplayerElementsSync({
  spreadId,
  shapeManagerRef,
  enabled = true,
  onRemoteTextBoxDeleted,
}: UseMultiplayerElementsSyncArgs) {
  const { subscribe } = useWebSocket()
  const queryClient = useQueryClient()
  const elementVersionByObjectIdRef = React.useRef<Map<string, string>>(new Map())

  const handleEvent = React.useCallback(
    async (message: { event?: string; data?: unknown }) => {
      if (!enabled) return
      const payload = asRecord(message.data) ?? asRecord(message as unknown)

      if (!payload) return

      const kind = detectEventKind(message.event) ?? inferEventKindFromPayload(payload)

      if (!kind) return

      const parsed = parseElementEvent(kind, payload)

      if (!parsed) return

      // Version gating – skip duplicate events that share the same real
      // version ID.  The backend currently sends "pending" as a placeholder
      // for every broadcast, so we must not treat placeholder values as
      // meaningful for deduplication or all updates after the first are dropped.
      if ((parsed.kind === 'added' || parsed.kind === 'updated') && parsed.versionId) {
        const isPlaceholder = parsed.versionId === 'pending' || parsed.versionId === ''

        if (!isPlaceholder) {
          const current = elementVersionByObjectIdRef.current.get(parsed.objectId)

          if (current === parsed.versionId) {
            return
          }

          elementVersionByObjectIdRef.current.set(parsed.objectId, parsed.versionId)
        }
      }

      // Resolve decoded data using a three-tier fallback:
      // 1. Inline data from WebSocket (elementObjectData, < 2KB)
      // 2. Fetch + decode from S3 signedUrl
      // 3. Fetch from API endpoint (pending elements / large elements)
      let resolvedDecodedData: unknown | null = parsed.decodedData
      let resolvedKindId: number | null = parsed.explicitKindId ?? parsed.inferredKindId

      if (resolvedDecodedData == null && parsed.kind !== 'deleted') {
        if (parsed.signedUrl) {
          // Tier 2: fetch from S3
          try {
            await initializeDecoder()
            const res = await fetch(parsed.signedUrl, {
              method: 'GET',
              headers: { Accept: 'application/x-protobuf, */*' },
            })

            if (!res.ok) {
              throw new Error(`S3 fetch failed: ${res.status} ${res.statusText}`)
            }

            const buffer = await res.arrayBuffer()
            const decoded = await decodeUnknownElementFromBuffer(buffer)

            resolvedDecodedData = decoded.decoded
            resolvedKindId = resolvedKindId ?? decoded.inferredKindId
          } catch (err) {
            logger.warn('multiplayer: failed to fetch/decode element from signedUrl', err)
          }
        }

        // Tier 3: fetch from API (no signedUrl or S3 fetch failed)
        if (resolvedDecodedData == null) {
          const targetLayer = parsed.targetLayer ?? 'userLayer'

          try {
            const response = await elementsService.getElementsByIds(spreadId, targetLayer, [
              parsed.objectId,
            ])

            const apiData = response[parsed.objectId]

            if (apiData != null) {
              resolvedDecodedData = apiData
            }
          } catch (err) {
            logger.warn('multiplayer: failed to fetch element from API', err)
          }
        }
      }

      // Unwrap StowedElement wrapper so downstream consumers get flat
      // element-type data ({ base, path, … }) regardless of source format.
      if (resolvedDecodedData != null) {
        const kindId = resolvedKindId ?? 1
        const rec = asRecord(resolvedDecodedData)

        if (rec) {
          resolvedDecodedData = unwrapElementData(rec, kindId)
        }
      }

      if (resolvedKindId == null) {
        logger.warn('multiplayer: missing kindId, defaulting to 1', {
          objectId: parsed.objectId,
          eventKind: parsed.kind,
          hasDecoded: resolvedDecodedData != null,
        })
      }

      // Apply to UI without full reload. Text is persisted as a bare kindId-1 rectangle carrying
      // content.text (no separate top-level text kind), decoded uniformly with any other shape.
      const finalKindId = resolvedKindId ?? 1
      const mgr = shapeManagerRef.current

      if (parsed.kind === 'deleted') {
        // A real ElementsDeletedEvent carries no per-object kindId and no decoded data (the
        // three-tier resolution above is skipped entirely for deletes), so there is no signal
        // to tell whether the deleted object was a text frame or a genuine shape — since
        // text-unification Phase 2, both live in ShapeManager as ShapeData.
        // Look up the numeric wire uid BEFORE removal (WMPCM-6868) — chain repair keys off
        // `uid`, not `objectId`/UUID, and it's unrecoverable from the manager once gone. Any
        // shape can carry a `uid` (only chain members do); `onRemoteTextBoxDeleted`'s handler
        // no-ops if this uid isn't actually in any chain.
        const deletedShape = mgr?.getShapes().find((s) => s.id === parsed.objectId)

        mgr?.removeShape(parsed.objectId)

        if (deletedShape?.uid != null) {
          onRemoteTextBoxDeleted?.(parsed.objectId, deletedShape.uid)
        }
      } else if (mgr && resolvedDecodedData != null) {
        // A remote text frame is a shape carrying paragraphs, same as one created locally — there
        // is no separate text-box decode path anymore (text-unification Phase 3).
        const shape = decodedDataToShape(parsed.objectId, resolvedDecodedData, finalKindId)

        if (shape) {
          // Don't clobber an in-progress local edit on this same object with a remote echo of it.
          if (mgr.getEditingShapeTextId() === parsed.objectId) {
            return
          }

          const isSupportedKind =
            finalKindId === 1 ||
            finalKindId === 2 ||
            finalKindId === OVAL_ELEMENT_KIND_ID ||
            finalKindId === SIDES_ELEMENT_KIND_ID

          if (!isSupportedKind) {
            logger.warn(
              'multiplayer: unsupported kindId applied as shape (orange placeholder risk)',
              {
                objectId: parsed.objectId,
                kindId: finalKindId,
                eventKind: parsed.kind,
              }
            )
          }

          const exists = mgr.getShapes().some((s) => s.id === parsed.objectId)

          if (exists) {
            mgr.updateShape(parsed.objectId, shape, { bypassLockGuard: true })
          } else {
            mgr.loadShapes([shape], { clearExisting: false })
          }
        }
      }

      // Patch React Query cache so other consumers (and future loads) see the new version
      const key = queryKeys.spreadCheckpoint.byId(spreadId, { decodeElements: true })

      queryClient.setQueryData(key, (prev: SpreadCheckpoint | undefined) => {
        if (!prev) return prev

        const decodedData = resolvedDecodedData
        const kindId = resolvedKindId

        return patchCheckpointCache(prev, parsed, {
          decodedData,
          kindId,
        })
      })

      if (isDebugEnabled()) {
        logger.debug('multiplayer: applied', {
          kind: parsed.kind,
          objectId: parsed.objectId,
          versionId: parsed.versionId,
        })
      }
    },
    [enabled, queryClient, shapeManagerRef, spreadId, onRemoteTextBoxDeleted]
  )

  React.useEffect(() => {
    if (!enabled) return
    return subscribe('*', (msg) => {
      void handleEvent(msg as unknown as { event?: string; data?: unknown })
    })
  }, [enabled, subscribe, handleEvent])
}
